import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { CLP_BASE, SELECT_CHECKBOX, type ClpTestPick } from './contractLabTestPricing';
import { TestMethodDetailDialog } from './testMethodDetail/TestMethodDetailDialog';

// G5 TestMethod_ListView_ContractPricing_New columns, in its order:
// Test · Matrix · Method · Component · Category · IsFieldTest (a read-only tick, as G5 draws it) · TestCode.
const COLUMNS: AppColumn<ClpTestPick>[] = [
  { field: 'test', header: 'Test', width: 220 },
  { field: 'matrix', header: 'Matrix', width: 120 },
  { field: 'method', header: 'Method', width: 150 },
  { field: 'component', header: 'Component', width: 110 },
  { field: 'category', header: 'Category', width: 110 },
  { field: 'isFieldTest', header: 'IsFieldTest', width: 95, align: 'center',
    value: (r) => (r.isFieldTest ? 'Yes' : 'No'), text: (r) => (r.isFieldTest ? 'Yes' : 'No'),
    render: (r) => (
      <input type="checkbox" className="clp-readonly-check" checked={r.isFieldTest} readOnly tabIndex={-1}
        aria-label={`Field test: ${r.isFieldTest ? 'Yes' : 'No'}`} onClick={(e) => e.preventDefault()} />
    ) },
  { field: 'testCode', header: 'TestCode' },
];

/**
 * Contract Lab Test Pricing ▸ Add — the G5 "Tests" popup (SuboutPOAdd_Execute):
 * every live test method, multi-select. OK hands the ticked tests back; the page
 * adds one unsaved row per test, and the rows are stored by the grid's Save, as
 * in G5. With nothing ticked OK shows G5's message and the popup stays open.
 * As in G5, the tick box selects a test and a click anywhere else on the row
 * opens that test's own window (TestMethodDetailDialog, G5 TestMethod_DetailView).
 */
export function ContractLabTestPickerDialog({ onClose, onPicked }: {
  onClose: () => void;
  onPicked: (tests: ClpTestPick[]) => void;
}) {
  const notify = useNotify();
  const [rows, setRows] = useState<ClpTestPick[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.get<{ data?: ClpTestPick[] }>(`${CLP_BASE}/test-picker`)
      .then(({ data }) => { if (alive) setRows(data.data ?? []); })
      .catch(() => { if (alive) notify.error('The tests could not be loaded.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [notify]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.test, r.matrix, r.method, r.component, r.category, r.testCode]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const ok = () => {
    if (selected.size === 0) { notify.info(SELECT_CHECKBOX); return; }
    onPicked(rows.filter((r) => selected.has(r.id)));
  };

  return (
    <AppDialog
      open
      title="Tests"
      width={900}
      className="dlg-center"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={ok}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="clp-pick-top">
        <span className="clp-pick-count">{selected.size} selected</span>
        {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
      </div>
      <AppDataGrid<ClpTestPick>
        rows={visible}
        columns={COLUMNS}
        getRowId={(r) => r.id}
        loading={loading}
        defaultPageSize={20}
        initialSort={{ key: 'test', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowClick={(r) => setViewing(r.id)}
        emptyText="No tests"
      />
      {viewing && <TestMethodDetailDialog testMethodId={viewing} onClose={() => setViewing(null)} />}
    </AppDialog>
  );
}
